import {
  Button,
  Divider,
  Grid,
  Input,
  Paper,
  Skeleton,
  Slider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useEffect, useState } from "react";
import { CostsConfig } from "../../../../types/costsConfig";
import { postOrganizationInputCosts } from "../../../../api/getOverviewData";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";

export default function InputCosts({
  height,
  selectedCostsConfig,
  costsConfigList,
  costsConfigListSetter,
}: {
  height: number;
  selectedCostsConfig: CostsConfig | undefined;
  costsConfigList: CostsConfig[];
  costsConfigListSetter: React.Dispatch<React.SetStateAction<CostsConfig[]>>;
}) {
  const theme = useTheme();


  const { showSnackbar } = useApplicationContext();
  const [costConfig, setCostsConfig] = useState<CostsConfig | undefined>(
    selectedCostsConfig
  );
  const [tmpCostConfig, setTmpCostConfig] = useState<CostsConfig | undefined>(
    selectedCostsConfig
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const handleApplyButton = async () => {
    if (!tmpCostConfig) return;

    try {
      const response = await postOrganizationInputCosts(tmpCostConfig);
    } catch (error) {
      console.error("Failed to apply changes: ", error);
      showSnackbar("Failed to apply changes, try refreshing the page", "error");
    } finally {
      const updatedCostsConfigList = costsConfigList.map((config) =>
        config.organizationId === tmpCostConfig.organizationId
          ? tmpCostConfig
          : config
      );
      setCostsConfig(tmpCostConfig);
      costsConfigListSetter(updatedCostsConfigList);
    }
  };

  useEffect(() => {
    setCostsConfig(selectedCostsConfig);
    setTmpCostConfig(selectedCostsConfig);
  }, [selectedCostsConfig]);

  useEffect(() => {
    const isEqual =
      JSON.stringify(tmpCostConfig) === JSON.stringify(costConfig);
    setIsButtonDisabled(isEqual);
  }, [tmpCostConfig, costConfig]);

  // Fallback to Skeleton if data is not ready
  if (!selectedCostsConfig || costsConfigList.length === 0 || !tmpCostConfig || !costConfig) {
    return (
      <Skeleton
        sx={{
          borderRadius: "5px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        animation="pulse"
        variant="rectangular"
        width={"100%"}
        height={height}
      />
    );
  }

  return (
    <Paper sx={{ borderRadius: "5px", height: height }}>
      <Grid container spacing={1} sx={{ padding: 2 }} flexDirection={"column"}>
        <Grid
          marginBottom={2}
          item
          flexDirection={"row"}
          spacing={2}
          container
          alignItems={"center"}
        >
          <Grid item>
            <Typography variant="h5">Input Costs</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" color={"gray"}>
              ({costConfig.organizationName})
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={1}
          alignContent={"space-around"}
          alignItems={"center"}
          sx={{ height: "100%", padding: "5px 0px 0px 30px" }}
        >
          <InputCostsElement
            text="Number of Employees"
            attribute="numEmployees"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
          />
          <InputCostsElement
            text="Average Monthly Salary per Employee (USD)"
            attribute="avgEmployeeSalaryUSD"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
          />
          <InputCostsElement
            text="Average Sick Leave Days in a Year per Employee"
            attribute="avgNumSickLeaves"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
          />
          <InputCostsElementWithSlider
            text="Percentage of MSD-Related Sick Leaves"
            attribute="msdPercentage"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
            min={0}
            max={100}
            step={1}
            suffix="%"
          />

          <InputCostsElementWithSlider
            text="Indirect Costs Multiplier"
            attribute="indirectCostsMultiplier"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
            min={0}
            max={2}
            step={0.1}
            suffix=""
          />

          <Grid marginTop={2} marginBottom={1} xs={12}>
            <Divider sx={{ borderBottomWidth: 1 }} />
          </Grid>

          <InputCostsElement
            text="Number of MLI® Back Devices"
            attribute="nBacks"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
          />
          <InputCostsElement
            text="Number of MLI® Elbow Devices"
            attribute="nElbows"
            data={tmpCostConfig}
            dataSetter={setTmpCostConfig}
          />
        </Grid>
        <Grid
          item
          xs={12}
          margin={2}
          container
          justifyContent="center"
          alignItems="center"
        >
          <Button
            key={"ApplyButton"}
            disabled={isButtonDisabled}
            onClick={handleApplyButton}
          >
            Apply Changes
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

function InputCostsElement({
  text,
  attribute,
  data,
  dataSetter,
}: {
  text: string;
  attribute: keyof CostsConfig;
  data: CostsConfig;
  dataSetter: React.Dispatch<React.SetStateAction<CostsConfig | undefined>>;
}) {

  const handleSetData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value: number = parseFloat(event.target.value);
    if (!isNaN(value)) {
      dataSetter((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [attribute]: value,
        };
      });
    }
  };

  return (
    <>
      <Grid item xs={5}>
        <Typography>{text}</Typography>
      </Grid>
      <Grid item xs={1}></Grid>
      <Grid item xs={6} alignItems={"center"}>
        <TextField
          id="standard-basic"
          value={data[attribute]}
          variant="standard"
          type="number"
          onChange={handleSetData}
        />
      </Grid>
    </>
  );
}

function InputCostsElementWithSlider({
  text,
  attribute,
  data,
  dataSetter,
  suffix,
  min,
  max,
  step,
}: {
  text: string;
  attribute: keyof CostsConfig;
  data: CostsConfig;
  dataSetter: React.Dispatch<React.SetStateAction<CostsConfig | undefined >>;
  suffix: string;
  min: number;
  max: number;
  step: number;
}) {
 
  const handleSliderChange = (event: Event, value: number | number[]) => {
    if (typeof value === "number") {
      if (!isNaN(value)) {
        dataSetter((prev) => {
          if (!prev) return prev;
          return {
          ...prev,
          [attribute]: value,
          };

        });
      }
    }
  };

  const handleSetData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value: number = parseFloat(event.target.value);
    if (!isNaN(value)) {
      dataSetter((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [attribute]: value,
        }
      });
    }
  };

  return (
    <>
      <Grid item xs={5}>
        <Typography>{text}</Typography>
      </Grid>
      <Grid item xs={1}></Grid>
      <Grid item xs={5} alignItems={"center"}>
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={6}>
            <Slider
              value={typeof data[attribute] === "number" ? data[attribute] : 0}
              min={min}
              max={max}
              step={step}
              onChange={handleSliderChange}
              aria-labelledby="input-slider"
            />
          </Grid>
          <Grid item xs={4} marginLeft={1} container alignItems="center">
            <Input
              value={typeof data[attribute] === "number" ? data[attribute] : 0}
              size="small"
              onChange={handleSetData}
              inputProps={{
                step: step,
                min: min,
                max: max,
                type: "number",
                "aria-labelledby": "input-slider",
              }}
            />
            <Typography marginLeft={1}>{suffix}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
